import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { rawFetch } from "@/lib/api/client";
import { useWorkspacePermissions } from "@/lib/workspacePermissions";
import { ErrorBox, EmptyState } from "@/components/common";

type Page<T> = { results: T[] };
type Version = { id: string; semver: string; status: string };
type Agent = { id: string; name: string; retrieval_profile: string | null };
type TermEval = { hit_at_5: number; citation_rate: number; n: number; available: number; truncated: boolean };

export function RetrievalEvaluate({ profileId, workspace }: { profileId: string; workspace: string | null }) {
  const { canEdit } = useWorkspacePermissions();
  const qc = useQueryClient();
  const [versionId, setVersionId] = useState(() => new URLSearchParams(window.location.search).get("ontology") || "");
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const versions = useQuery<Page<Version>>({ queryKey: ["retrieval-versions", workspace],
    queryFn: () => rawFetch(`/ontology/versions/?workspace=${encodeURIComponent(workspace || "")}&page_size=100`),
    enabled: Boolean(workspace) });
  const last = useQuery<{ last_eval: TermEval | null }>({ queryKey: ["retrieval-term-eval", profileId, versionId],
    queryFn: () => rawFetch(`/retrieval/profiles/${profileId}/term-eval/?ontology_version_id=${encodeURIComponent(versionId)}`),
    enabled: Boolean(profileId && versionId) });
  const run = async () => {
    if (!profileId || !versionId || busy) return;
    setBusy(true); setError(null);
    try {
      await rawFetch(`/retrieval/profiles/${profileId}/term-eval/`, {
        method: "POST", body: JSON.stringify({ ontology_version_id: versionId }),
      });
      await qc.invalidateQueries({ queryKey: ["retrieval-term-eval", profileId, versionId] });
    } catch (caught) { setError(caught); } finally { setBusy(false); }
  };
  return <section className="card space-y-4" aria-label="Evaluate retrieval">
    <div><h2 className="text-base font-semibold">Check this profile against your draft</h2>
      <p className="text-sm text-muted">Approved term definitions become test questions. The score measures whether the expected term appears in the first five results with a citation.</p></div>
    {!profileId ? <EmptyState title="Choose a profile first" /> : <>
      <label className="block max-w-md text-sm">Ontology draft
        <select className="input mt-1 w-full" value={versionId} onChange={(event) => setVersionId(event.target.value)}>
          <option value="">Choose a draft</option>
          {versions.data?.results.filter((row) => row.status === "draft").map((row) =>
            <option key={row.id} value={row.id}>Version {row.semver}</option>)}
        </select></label>
      <button type="button" className="btn-primary" disabled={!canEdit || !versionId || busy} onClick={run}>Run evaluation</button>
      {error != null && <ErrorBox error={error} />}
      {last.data?.last_eval ? <div className="border-l-2 border-accent pl-3 text-sm" role="status">
        <strong>Last evaluation: {(last.data.last_eval.hit_at_5 * 100).toFixed(0)}% of {last.data.last_eval.n} questions found the expected term.</strong>
        <p className="text-muted">Citations present: {(last.data.last_eval.citation_rate * 100).toFixed(0)}%.
          {last.data.last_eval.truncated && ` Tested ${last.data.last_eval.n} of ${last.data.last_eval.available} questions.`}</p>
      </div> : versionId && !last.isLoading && <p className="text-sm text-muted">No evaluation saved for this draft yet.</p>}
    </>}
  </section>;
}

export function RetrievalConnect({ profileId, workspace }: { profileId: string; workspace: string | null }) {
  const { canEdit } = useWorkspacePermissions();
  const qc = useQueryClient();
  const [agentId, setAgentId] = useState("");
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const agents = useQuery<Page<Agent>>({ queryKey: ["retrieval-agents", workspace],
    queryFn: () => rawFetch(`/agents/agents/?workspace=${encodeURIComponent(workspace || "")}&page_size=100`),
    enabled: Boolean(workspace) });
  const bound = agents.data?.results.filter((row) => row.retrieval_profile === profileId) ?? [];
  const changeBinding = async (id: string, value: string | null) => {
    setBusy(true); setError(null);
    try {
      await rawFetch(`/agents/agents/${id}/`, { method: "PATCH", body: JSON.stringify({ retrieval_profile: value }) });
      await qc.invalidateQueries({ queryKey: ["retrieval-agents", workspace] });
      setAgentId("");
    } catch (caught) { setError(caught); } finally { setBusy(false); }
  };
  const api = `POST /api/v1/retrieval/search/\nAuthorization: ApiKey <YOUR_SERVICE_ACCOUNT_KEY>\n\n{"profile_id":"${profileId}","query":"What is a policy?","top_k":5}`;
  const python = `client.query("${profileId}", "What is a policy?")`;
  const typescript = `await client.query("${profileId}", "What is a policy?")`;
  const mcp = JSON.stringify({ jsonrpc: "2.0", id: 1, method: "tools/call",
    params: { name: "retrieval.query", arguments: { profile_id: profileId, query: "What is a policy?" } } }, null, 2);
  return <section className="card space-y-4" aria-label="Connect retrieval">
    <div><h2 className="text-base font-semibold">Use this profile</h2>
      <p className="text-sm text-muted">An agent or service account searches with this saved scope. Each caller still needs access to the underlying evidence.</p></div>
    {!profileId ? <EmptyState title="Choose a profile first" /> : <>
      <div className="border border-border p-3"><h3 className="text-sm font-semibold">Agents using this profile</h3>
        {bound.length ? <ul className="mt-2 space-y-2">{bound.map((agent) => <li key={agent.id} className="flex items-center justify-between gap-2 text-sm">
          <span>{agent.name}</span>{canEdit && <button type="button" className="btn-secondary" disabled={busy}
            onClick={() => changeBinding(agent.id, null)}>Unbind</button>}</li>)}</ul>
          : <p className="mt-1 text-sm text-muted">No agents use this profile yet.</p>}
        {canEdit && <div className="mt-3 flex flex-wrap gap-2"><select className="input" aria-label="Agent to bind" value={agentId}
          onChange={(event) => setAgentId(event.target.value)}><option value="">Choose an agent</option>
          {agents.data?.results.filter((row) => row.retrieval_profile !== profileId).map((row) =>
            <option key={row.id} value={row.id}>{row.name}</option>)}</select>
          <button type="button" className="btn-primary" disabled={!agentId || busy} onClick={() => changeBinding(agentId, profileId)}>Bind agent</button></div>}
        <Link to="/agents" className="mt-2 inline-block text-sm text-accent underline">Manage agents</Link>
      </div>
      {error != null && <ErrorBox error={error} />}
      <p className="text-xs text-muted">Use a service account permitted to search this workspace. Keep its key in your secret manager; these examples contain no key.</p>
      <details className="border border-border p-3"><summary className="cursor-pointer text-sm font-semibold">API, SDK and MCP examples</summary>
      <div className="mt-3 grid gap-3 lg:grid-cols-2">{[["API", api], ["Python SDK", python], ["TypeScript SDK", typescript], ["MCP", mcp]].map(([label, code]) =>
        <div key={label} className="min-w-0 border border-border p-3"><h3 className="text-sm font-semibold">{label}</h3>
          <pre className="mt-2 overflow-x-auto whitespace-pre-wrap break-all bg-bg p-2 text-xs"><code>{code}</code></pre></div>)}</div>
      </details>
    </>}
  </section>;
}
